/**
 * Verbes métier du domaine chantier.
 */
import { ActionLevel } from "../../levels.js";
import { DomainError } from "../../errors.js";
import { defineService } from "../../service.js";
import type { ProjectRepository } from "./repository.js";
import {
  INTERRUPTED_PROJECT_STATUSES,
  OPEN_PROJECT_STATUSES,
  driftSeverity,
  isDrifting,
} from "./types.js";
import type {
  ProjectDetail,
  ProjectLot,
  ProjectPhase,
  ProjectStatus,
  ProjectSummary,
} from "./types.js";
import type {
  ChangeProjectStatusInput,
  ListProjectsInput,
  ReadProjectInput,
  RemoveLotInput,
  RemovePhaseInput,
  SaveLotInput,
  SavePhaseInput,
  SaveProjectInput,
  SetPhasePredecessorsInput,
  ShiftPhasesInput,
} from "./schema.js";

/** Chantiers pour lesquels des travaux sont encore attendus. */
const ACTIVE: ProjectStatus[] = [...OPEN_PROJECT_STATUSES];

export function createProjectServices(repo: ProjectRepository) {
  const listerChantiers = defineService<ListProjectsInput, ProjectSummary[]>({
    name: "lister_chantiers",
    level: ActionLevel.Read,
    handler: (ctx, input) =>
      repo.list(ctx, { status: input.status, clientId: input.clientId }),
  });

  const listerChantiersActifs = defineService<
    { confirmationId?: string },
    ProjectSummary[]
  >({
    name: "lister_chantiers_actifs",
    level: ActionLevel.Read,
    handler: (ctx) => repo.list(ctx, { status: ACTIVE }),
  });

  /**
   * Répond à « quel chantier perd de l'argent ? ».
   *
   * Deux cas, tous deux remontés : le chantier déjà déficitaire, et celui qui
   * consomme son budget plus vite qu'il n'avance. Le second est le plus utile —
   * il laisse encore le temps de réagir. Trié du plus grave au moins grave,
   * parce qu'une liste non ordonnée oblige le patron à faire le tri lui-même.
   */
  const chantiersEnDerive = defineService<
    { confirmationId?: string },
    ProjectSummary[]
  >({
    name: "chantiers_en_derive",
    level: ActionLevel.Read,
    handler: async (ctx) => {
      const projects = await repo.list(ctx, { status: ACTIVE });
      return projects
        .filter((p) => isDrifting(p.health))
        .sort((a, b) => driftSeverity(a.health) - driftSeverity(b.health));
    },
  });

  const lireChantier = defineService<ReadProjectInput, ProjectDetail>({
    name: "lire_chantier",
    level: ActionLevel.Read,
    handler: async (ctx, input) => {
      const project = await repo.findById(ctx, input.projectId);
      if (!project) {
        throw new DomainError("not_found", "Ce chantier n'existe pas.", {
          projectId: input.projectId,
        });
      }
      return project;
    },
  });

  const enregistrerChantier = defineService<SaveProjectInput, ProjectSummary>({
    name: "enregistrer_chantier",
    level: ActionLevel.ReversibleWrite,
    handler: (ctx, input) => {
      // Un chantier qui finit avant de commencer fausse tout le planning en
      // aval : autant le refuser à la saisie.
      if (
        input.plannedStartDate &&
        input.plannedEndDate &&
        input.plannedEndDate < input.plannedStartDate
      ) {
        throw new DomainError(
          "invalid_input",
          "La date de fin prévue est antérieure à la date de démarrage.",
          { start: input.plannedStartDate, end: input.plannedEndDate },
        );
      }

      return repo.save(ctx, {
        projectId: input.projectId,
        clientId: input.clientId,
        name: input.name,
        description: input.description ?? null,
        siteAddress: input.siteAddress ?? null,
        sitePostalCode: input.sitePostalCode ?? null,
        siteCity: input.siteCity ?? null,
        plannedStartDate: input.plannedStartDate ?? null,
        plannedEndDate: input.plannedEndDate ?? null,
        budgetHt: input.budgetHt ?? null,
        quoteId: input.quoteId ?? null,
      });
    },
  });

  /**
   * Changer le statut d'un chantier.
   *
   * Le patron : « le statut des chantiers n'est pas modifiable ». Il ne l'était
   * nulle part : `planned` à la création, et plus rien ensuite. Or tout en
   * dépend — le quart d'heure sécurité ne se rappelle que sur un chantier en
   * cours, la surveillance des phases aussi, et l'alerte « passez-le en
   * cours » demandait un geste qu'aucun écran n'offrait.
   *
   * Aucune transition n'est interdite : on reprend un chantier terminé pour
   * une réserve, on repasse en « planifié » un chantier démarré trop tôt. Tout
   * se défait en un geste, et un moteur d'états qui refuserait la réalité du
   * terrain serait contourné dès la première semaine.
   *
   * Le motif n'est gardé que pour une pause ou un arrêt ; ailleurs il n'aurait
   * pas de sens, et la base l'efface de toute façon.
   */
  const changerStatutChantier = defineService<ChangeProjectStatusInput, ProjectSummary>({
    name: "changer_statut_chantier",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) =>
      repo.setStatus(
        ctx,
        input.projectId,
        input.status,
        INTERRUPTED_PROJECT_STATUSES.includes(input.status) ? (input.reason ?? null) : null,
      ),
  });

  /**
   * Poser ou déplacer une phase.
   *
   * Ouvert aux trois rôles de bureau, comme `enregistrer_chantier` : découper
   * un chantier en phases est du travail de conduite de travaux, pas une
   * décision d'engagement. Ce qui engage, c'est la facture qu'un avancement
   * finit par déclencher — et celle-là passe par la file du soir, puis par une
   * confirmation.
   */
  const enregistrerPhase = defineService<SavePhaseInput, ProjectPhase>({
    name: "enregistrer_phase",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) => {
      if (input.plannedStart && input.plannedEnd && input.plannedEnd < input.plannedStart) {
        throw new DomainError(
          "invalid_input",
          "La fin prévue de la phase est antérieure à son démarrage.",
          { start: input.plannedStart, end: input.plannedEnd },
        );
      }
      return repo.savePhase(ctx, {
        phaseId: input.phaseId,
        projectId: input.projectId,
        name: input.name,
        sortOrder: input.sortOrder,
        progressPercent: input.progressPercent,
        status: input.status,
        plannedStart: input.plannedStart,
        plannedEnd: input.plannedEnd,
        lotId: input.lotId,
      });
    },
  });

  /**
   * Supprimer une phase.
   *
   * Réversible au sens du niveau — rien ne sort de l'entreprise — mais la
   * ligne part pour de bon, et avec elle les déclarations d'avancement qui s'y
   * rattachaient : `field_reports.phase_id` est en `ON DELETE CASCADE`. C'est
   * délibéré, une déclaration sur une phase qui n'existe plus n'est plus
   * validable, mais ça mérite d'être su.
   */
  const supprimerPhase = defineService<RemovePhaseInput, void>({
    name: "supprimer_phase",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur"],
    handler: (ctx, input) => repo.removePhase(ctx, input.phaseId),
  });

  /**
   * Poser ou renommer un lot — un corps de métier sur le chantier.
   *
   * Mêmes rôles que les phases : découper un chantier est de la conduite de
   * travaux. À la création, les phases types du lot se posent avec lui, dans
   * l'ordre, en un geste : un lot sans phase n'avance pas, et taper six
   * intitulés sur un téléphone est ce qui fait qu'il reste vide.
   */
  const enregistrerLot = defineService<SaveLotInput, ProjectLot>({
    name: "enregistrer_lot",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: async (ctx, input) => {
      const lot = await repo.saveLot(ctx, {
        lotId: input.lotId,
        projectId: input.projectId,
        name: input.name,
        sortOrder: input.sortOrder,
        notes: input.notes,
      });
      if (!input.lotId) {
        const vus = new Set<string>();
        let rang = 0;
        for (const nom of input.phases) {
          const cle = nom.toLowerCase();
          if (vus.has(cle)) continue;
          vus.add(cle);
          await repo.savePhase(ctx, {
            projectId: input.projectId,
            lotId: lot.id,
            name: nom,
            sortOrder: rang++,
            progressPercent: 0,
            status: "pending",
            plannedStart: null,
            plannedEnd: null,
          });
        }
      }
      return lot;
    },
  });

  /**
   * Supprimer un lot, et ses phases avec lui. Patron et conducteur, comme une
   * phase : les déclarations d'avancement de ses phases partent aussi.
   */
  const supprimerLot = defineService<RemoveLotInput, void>({
    name: "supprimer_lot",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur"],
    handler: (ctx, input) => repo.removeLot(ctx, input.lotId),
  });

  /**
   * Dire après quelles phases une phase démarre, et avec quel délai.
   *
   * La liste remplace la précédente. Une boucle est refusée par la base ; un
   * enchaînement non tenu par les dates ne l'est jamais — l'écran propose le
   * décalage, voir `proposeShifts`.
   */
  const enchainerPhase = defineService<SetPhasePredecessorsInput, void>({
    name: "enchainer_phase",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) => {
      const vues = new Set<string>();
      for (const p of input.predecessors) {
        if (p.dependsOnId === input.phaseId) {
          throw new DomainError("invalid_input", "Une phase ne peut pas s'attendre elle-même.", {
            phaseId: input.phaseId,
          });
        }
        if (vues.has(p.dependsOnId)) {
          throw new DomainError("invalid_input", "La même phase est attendue deux fois.", {
            dependsOnId: p.dependsOnId,
          });
        }
        vues.add(p.dependsOnId);
      }
      return repo.setPredecessors(ctx, input.phaseId, input.predecessors);
    },
  });

  /**
   * Appliquer les décalages proposés : toutes les phases d'un coup, ou
   * aucune. C'est le clic qui valide ce que `proposeShifts` a calculé.
   */
  const decalerPhases = defineService<ShiftPhasesInput, number>({
    name: "decaler_phases",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) => {
      for (const m of input.moves) {
        if (m.plannedEnd && m.plannedEnd < m.plannedStart) {
          throw new DomainError(
            "invalid_input",
            "La fin prévue d'une phase est antérieure à son démarrage.",
            { phaseId: m.phaseId },
          );
        }
      }
      return repo.shiftPhases(ctx, input.projectId, input.moves);
    },
  });

  return {
    listerChantiers,
    listerChantiersActifs,
    chantiersEnDerive,
    lireChantier,
    enregistrerChantier,
    changerStatutChantier,
    enregistrerPhase,
    supprimerPhase,
    enregistrerLot,
    supprimerLot,
    enchainerPhase,
    decalerPhases,
  };
}

export type ProjectServices = ReturnType<typeof createProjectServices>;
